// app/src/lib/cashflow/taxes.js — detect tax payments from bank activity.
//
// Outflows are classified by description keyword into:
//   excise   — state/local excise + sales tax (e.g. "WA DEPT REVENUE")
//   payroll  — federal/state payroll tax (e.g. "ADP TAX", "EFTPS")
//   federal  — broader federal income tax (e.g. "IRS", "US TREASURY")
//
// There are no built-in defaults — every operator's bank descriptors are
// different, and guessing produced false positives (a bare "941" matched
// any TRACE# containing those digits). The user adds their own identifiers
// per bucket in Account → Tax keywords. The Taxes report shows a setup
// state when nothing is configured.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

export const EMPTY_KEYWORDS = { excise: [], payroll: [], federal: [] };

export async function getKeywords() {
  const c = await currentClaims();
  if (!c) return EMPTY_KEYWORDS;
  const p = await db.userProfile.get(c.sub);
  const saved = (p && p.taxKeywords) || {};
  return {
    excise: Array.isArray(saved.excise) ? saved.excise : [],
    payroll: Array.isArray(saved.payroll) ? saved.payroll : [],
    federal: Array.isArray(saved.federal) ? saved.federal : [],
  };
}

export function hasAnyKeyword(kw) {
  return ((kw?.excise?.length || 0) + (kw?.payroll?.length || 0) + (kw?.federal?.length || 0)) > 0;
}

export async function setKeywords(patch) {
  const c = await currentClaims();
  if (!c) throw new Error('taxes: not signed in');
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  const cleanList = (arr) => [...new Set((arr || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean))].slice(0, 25);
  const next = { ...(existing.taxKeywords || {}), ...patch };
  for (const k of Object.keys(next)) next[k] = cleanList(next[k]);
  await db.userProfile.put({ ...existing, userId: c.sub, taxKeywords: next });
  return next;
}

/**
 * Match a keyword against the haystack with a leading word boundary so bare
 * tokens like "941" / "940" / "edd" don't hit arbitrary digit runs in TRACE#
 * or IND ID fields ("A4940" → no, "Form 940" → yes). The right edge stays
 * open so prefixes still match ("us treas" → "US TREASURY").
 *
 * Keywords containing characters that don't play nicely with \b (e.g. "l&i")
 * fall back to plain substring — the special character itself is a strong
 * enough discriminator that false positives aren't a concern.
 */
function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function matchesKeyword(hay, k) {
  const key = String(k || '').toLowerCase();
  if (!key) return false;
  // Keywords with non-word/non-space chars (l&i, 401(k)-style, etc.) use
  // substring — the unusual character carries the specificity.
  if (/[^\w ]/.test(key)) return hay.includes(key);
  return new RegExp('\\b' + escapeRegex(key), 'i').test(hay);
}

/**
 * Classify a single tx against the keyword sets. Returns 'excise', 'payroll',
 * 'federal', or null. Matches merchant first (more specific) then description.
 */
export function classifyTax(tx, keywords) {
  if (!tx) return null;
  if ((tx.amountCents || 0) <= 0) return null; // outflows only
  const merchant = (tx.merchantName || '').toLowerCase();
  const desc = (tx.description || '').toLowerCase();
  const hay = merchant + ' ' + desc;
  for (const bucket of ['payroll', 'excise', 'federal']) {
    if ((keywords[bucket] || []).some((k) => matchesKeyword(hay, k))) return bucket;
  }
  return null;
}

/**
 * Roll up tax payments by ISO month. Returns `{ months: [{ym, total, excise,
 * payroll, federal, rows}], totals: {…} }` newest-first.
 *
 * An explicit Spending categorization (`_spendCat`, stamped upstream) is
 * authoritative both ways: a tx the operator categorized as 'taxes' counts
 * even without a keyword match (bucketed by keyword when one matches, else
 * 'payroll' — remittance processors like ADP/EFTPS are the common case), and
 * a keyword match the operator rerouted elsewhere (e.g. "ADP Payroll Fees" →
 * Software) must NOT count here.
 */
export function summarize(txs, keywords) {
  const months = new Map();
  let totals = { total: 0, excise: 0, payroll: 0, federal: 0 };
  for (const tx of txs || []) {
    if (tx.pending) continue;
    let bucket = classifyTax(tx, keywords);
    if (tx._spendCat ? tx._spendCat !== 'taxes' : !bucket) continue;
    if (!bucket) bucket = 'payroll'; // stamped 'taxes' without a keyword match
    const ym = (tx.date || '').slice(0, 7);
    if (!ym) continue;
    if (!months.has(ym)) months.set(ym, { ym, total: 0, excise: 0, payroll: 0, federal: 0, rows: [] });
    const m = months.get(ym);
    m.total += tx.amountCents || 0;
    m[bucket] += tx.amountCents || 0;
    m.rows.push({ ...tx, _bucket: bucket });
    totals.total += tx.amountCents || 0;
    totals[bucket] += tx.amountCents || 0;
  }
  for (const m of months.values()) m.rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const arr = [...months.values()].sort((a, b) => b.ym.localeCompare(a.ym));
  return { months: arr, totals };
}
